import { useCallback, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { LogoMark } from "@/components/Brand";
import { LockoutCountdown } from "@/components/LockoutCountdown";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next")?.startsWith("/") ? params.get("next") : "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockSeconds, setLockSeconds] = useState(0);
  const clearLock = useCallback(() => setLockSeconds(0), []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(nextPath);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429 && detail?.retry_after) {
        setLockSeconds(detail.retry_after);
      } else {
        setError(formatApiError(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent/40 opacity-90" />
        <Link to="/" className="relative flex items-center gap-2.5">
          <LogoMark className="w-9 h-9" />
          <span className="font-display font-extrabold tracking-tight">Futuregen Innovations</span>
        </Link>
        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold tracking-tight leading-tight mb-4">
            The future of regulated service compliance.
          </h1>
          <p className="text-lg text-primary-foreground/80 leading-relaxed max-w-md">
            Expert CQC &amp; Ofsted support, AI compliance guidance, and ready-to-use policies — all in one calm place.
          </p>
        </div>
        <p className="relative text-sm text-primary-foreground/70">
          Trusted by registered managers across the UK.
        </p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm animate-fade-up">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-1">Sign in</h2>
          <p className="text-muted-foreground mb-8 text-sm">Access your compliance workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email-input" placeholder="you@careservice.co.uk" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password-input" placeholder="••••••••" className="mt-1.5" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            {lockSeconds > 0 && <LockoutCountdown seconds={lockSeconds} email={email} onExpire={clearLock} />}
            <div className="text-right -mt-1">
              <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
            </div>
            <Button type="submit" className="w-full" disabled={loading || lockSeconds > 0} data-testid="login-submit-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6 text-center">
            No account?{" "}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="go-to-register">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
